'use client';

import { Col, Container, Row, AspectRatio, Typo, List, Button } from '@geberit/gdds';

// styles
import styles from './asset-detail-page.module.scss';

// types
import type { PressPortalAssetDetailsProps } from '../types/press-portal.types';
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';

// components
import { Headline } from 'components/ContentElementsGdds/headline/headline';
import RelatedMediaTiles from '../media-tiles/related-media-tiles';
import RelatedPressTiles from '../press-tiles/related-press-tiles';

// utils
import { buildSize, gridSizes } from 'utils/gridSize';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useLocale } from 'utils/use-locale';
import { useIsDesktop } from 'components/App/SizeProvider';
import { useCurrentLanguage } from 'utils/hooks/useCurrentLanguage';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { useMediaDetails } from '../hooks/use-media-details';
import { downloadMedia, formatDate } from '../utils';

const mediaTypeTranslationKeyMap = {
  video: 'press_download_video',
  image: 'press_download_image',
  pdf: 'press_download_pdf',
  docx: 'press_download_file',
};

function PressPortalAssetDetailPage({ material, isGdds }: Readonly<PressPortalAssetDetailsProps>) {
  const isGddsDesktop = useIsDesktop({ gdds: true });
  const language = useCurrentLanguage();
  const locale = useLocale(language);
  const details: string[] = useMediaDetails(material, true);
  const hasFileOrPdf =
    material &&
    material.type_of_media === 'document' &&
    (material.document_format === '.pdf' || material.document_format === '.docx');

  let downloadTranslationKey = material
    ? mediaTypeTranslationKeyMap[material.type_of_media]
    : undefined;

  if (hasFileOrPdf) {
    downloadTranslationKey = mediaTypeTranslationKeyMap[material.document_format.replace('.', '')];
  }
  const translate = useTranslationFunction();

  function getImageUrl(): {
    imageUrl?: string;
    aspectRatio: string;
    mode?: 'portrait' | 'landscape';
  } {
    if (!material) {
      return { imageUrl: undefined, aspectRatio: '16:9' };
    }

    if (material.type_of_media === 'image') {
      let mode: 'portrait' | 'landscape' = 'portrait';
      const [width, height] = material.image_dimensions?.split('x')?.map(Number) || [0, 0];

      if (width !== undefined && height !== undefined && width > height) {
        mode = 'landscape';
      }

      return {
        imageUrl: material.image,
        aspectRatio: mode === 'portrait' ? '3:4' : '16:9',
        mode,
      };
    }

    if (material.type_of_media === 'document') {
      return {
        imageUrl: material.document_thumbnail,
        aspectRatio: '3:4',
        mode: 'portrait',
      };
    }

    if (material.type_of_media === 'video') {
      return {
        imageUrl: material.thumbnail,
        aspectRatio: '16:9',
        mode: 'landscape',
      };
    }

    return { imageUrl: undefined, aspectRatio: '16:9', mode: 'landscape' };
  }

  if (!material) {
    return null;
  }

  const imageInfo = getImageUrl();

  return (
    <section className={styles.pressPortalAssetDetailPage}>
      <Container maxContentWidth={isGdds ? buildSize(gridSizes.gddsFullGrid) : '80rem'}>
        <Row className={styles.titleWrapper}>
          <Col size={[0, 0, 2]} />
          <Col size={[4, 8, 8]}>
            <Typo variant="p1">{formatDate(material, locale)}</Typo>
            <Headline title={material?.header} tag={Formats.h1} />
          </Col>
          <Col size={[0, 0, 2]} />
        </Row>

        <Row className={classNameBuilder(styles.document, !isGdds && styles.documentFontSize)}>
          {/* render Col on portrait and only on Desktop */}
          {imageInfo.mode === 'portrait' && isGddsDesktop && <Col size={[0, 0, 2]} />}

          <Col size={imageInfo.mode === 'portrait' ? [4, 4, 4] : [4, 8, 6]}>
            <div
              className={classNameBuilder(
                styles.wrapper,
                imageInfo.mode === 'landscape' && styles.landscape,
                hasFileOrPdf && styles.documentBorder,
              )}
            >
              {material && imageInfo.imageUrl && (
                <div className={styles.imageContainer}>
                  <AspectRatio ratio={imageInfo.aspectRatio}>
                    {<img className={styles.heroImageC} src={imageInfo.imageUrl} />}
                  </AspectRatio>
                </div>
              )}
            </div>
          </Col>
          <Col size={[4, imageInfo.mode === 'portrait' ? 4 : 5, 4]}>
            <div className={styles.desc}>
              <Headline
                isFlexItem
                tag={Formats.h2}
                variant={Formats.h4}
                className={styles.headline}
                title={translate('press_media_details')}
              />

              <List level="1" listItemMarginLeft={16} listItemMarginTop={12}>
                {details.map((detail) => (
                  <List.Item key={detail}>
                    <Typo variant="p1" className={styles.text}>
                      {detail}
                    </Typo>
                  </List.Item>
                ))}
              </List>
              <Button
                symbol="Download"
                onClick={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  downloadMedia(material);
                }}
              >
                {translate(downloadTranslationKey)}
              </Button>
            </div>
          </Col>

          {/* render Col on portrait and only on Desktop */}
          {imageInfo.mode === 'portrait' && isGddsDesktop && <Col size={[0, 0, 2]} />}
        </Row>
        <RelatedPressTiles materials={material.related_news} language={language} />
        <RelatedMediaTiles mediaList={material.related_materials} language={language} />
      </Container>
    </section>
  );
}

export default PressPortalAssetDetailPage;
